import { useEffect, useState } from 'react';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import Icon from '../ui/Icon';

const DISMISS_KEY = 'pwa_install_dismissed_at';
const DISMISS_DAYS = 7;
const AUTO_SHOW_DELAY_MS = 4000;
const OPEN_EVENT = 'fg-open-install-prompt';

// Cualquier parte de la app (drawer, Login) puede pedir que se muestre el
// prompt/instrucciones sin esperar el delay ni respetar el "Ahora no"
// reciente — es una acción explícita del usuario, no un pop-up espontáneo.
export function requestInstallPrompt() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

function wasRecentlyDismissed() {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const dismissedAt = Number(raw);
  if (!Number.isFinite(dismissedAt)) return false;
  const elapsedDays = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
  return elapsedDays < DISMISS_DAYS;
}

function markDismissed() {
  localStorage.setItem(DISMISS_KEY, String(Date.now()));
}

function IOSInstructionsModal({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-gray-900">Instalar Family Growers</h3>

        <ol className="mt-4 flex flex-col gap-4 text-sm text-gray-700">
          <li className="flex items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-700">
              <Icon name="share" className="h-5 w-5" />
            </span>
            <span>
              Toca el botón <strong>Compartir</strong> del navegador.
            </span>
          </li>
          <li className="flex items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-700">
              <Icon name="plus" className="h-5 w-5" />
            </span>
            <span>
              Selecciona <strong>&ldquo;Añadir a pantalla de inicio&rdquo;</strong>.
            </span>
          </li>
          <li className="flex items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-700">
              <Icon name="install" className="h-5 w-5" />
            </span>
            <span>
              Presiona <strong>&ldquo;Añadir&rdquo;</strong>.
            </span>
          </li>
        </ol>

        <p className="mt-4 text-sm text-gray-500">
          Después podrás abrir Family Growers directamente desde el ícono de tu teléfono.
        </p>

        <button
          type="button"
          onClick={onClose}
          className="mt-6 flex min-h-[50px] w-full items-center justify-center rounded-xl bg-green-600 px-4 text-base font-semibold text-white transition-colors hover:bg-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2"
        >
          Entendido
        </button>
      </div>
    </div>
  );
}

function SamsungInternetModal({ onClose }) {
  const [showMore, setShowMore] = useState(false);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-gray-900">Instalar Family Growers</h3>

        <p className="mt-4 text-sm text-gray-700">
          Este navegador puede mostrar una advertencia de Android durante la instalación.
        </p>
        <p className="mt-2 text-sm text-gray-700">
          Puedes seguir usando Family Growers normalmente desde aquí.
        </p>

        <button
          type="button"
          onClick={onClose}
          className="mt-6 flex min-h-[50px] w-full items-center justify-center rounded-xl bg-green-600 px-4 text-base font-semibold text-white transition-colors hover:bg-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2"
        >
          Seguir usando aquí
        </button>

        {!showMore && (
          <button
            type="button"
            onClick={() => setShowMore(true)}
            className="mt-3 flex min-h-[44px] w-full items-center justify-center rounded-xl px-4 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
          >
            Más opciones de instalación
          </button>
        )}

        {showMore && (
          <div className="mt-4 flex flex-col gap-3 border-t border-gray-100 pt-4 text-sm text-gray-500">
            <p>
              También puedes agregar un acceso directo manualmente: abre el menú de este navegador
              y busca <strong>&ldquo;Añadir página a&rdquo; → &ldquo;Pantalla de inicio&rdquo;</strong>. Puede
              aparecer la misma advertencia de Android al hacerlo.
            </p>
            <p>
              También puedes abrir Family Growers desde otro navegador compatible con instalación,
              como Chrome.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function PWAInstallPrompt() {
  const { installable, installed, isIOS, isSamsungInternet, deferredPrompt, promptInstall } = usePWAInstall();
  const [visible, setVisible] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [showSamsungInfo, setShowSamsungInfo] = useState(false);

  // Auto-mostrar (contextual, no invasivo): espera a que la pantalla haya
  // terminado de cargar y respeta el "Ahora no" reciente. En Samsung
  // Internet esto solo abre la invitación discreta de siempre — pulsar
  // "Instalar aplicación" ahí lleva a información, nunca a un instalador
  // automático.
  useEffect(() => {
    if (!installable) return undefined;
    if (wasRecentlyDismissed()) return undefined;

    const timer = setTimeout(() => setVisible(true), AUTO_SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [installable]);

  // Apertura explícita (menú / Login), ignora el delay y el dismiss.
  useEffect(() => {
    function handleOpenRequest() {
      if (isIOS) {
        setShowIOSInstructions(true);
      } else if (isSamsungInternet) {
        setShowSamsungInfo(true);
      } else if (deferredPrompt) {
        setVisible(true);
      }
    }
    window.addEventListener(OPEN_EVENT, handleOpenRequest);
    return () => window.removeEventListener(OPEN_EVENT, handleOpenRequest);
  }, [isIOS, isSamsungInternet, deferredPrompt]);

  if (installed || !installable) return null;

  const handleInstalarClick = async () => {
    if (isIOS) {
      setVisible(false);
      setShowIOSInstructions(true);
      return;
    }
    if (isSamsungInternet) {
      setVisible(false);
      setShowSamsungInfo(true);
      return;
    }
    await promptInstall();
    setVisible(false);
  };

  const handleAhoraNo = () => {
    markDismissed();
    setVisible(false);
  };

  return (
    <>
      {visible && (
        <div className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-[calc(env(safe-area-inset-bottom)+16px)] sm:justify-end sm:pr-6">
          <div className="w-full max-w-sm rounded-2xl border border-gray-100 bg-white p-5 shadow-xl">
            <div className="flex items-start gap-3">
              <img src="/icon-192.png" alt="" className="h-12 w-12 shrink-0 rounded-xl" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-gray-900">Instala Family Growers</p>
                <p className="mt-1 text-sm text-gray-500">
                  Accede más rápido y úsala como una aplicación desde tu teléfono.
                </p>
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-2">
              <button
                type="button"
                onClick={handleInstalarClick}
                className="flex min-h-[48px] w-full items-center justify-center rounded-xl bg-green-600 px-4 text-base font-semibold text-white transition-colors hover:bg-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2"
              >
                Instalar aplicación
              </button>
              <button
                type="button"
                onClick={handleAhoraNo}
                className="flex min-h-[44px] w-full items-center justify-center rounded-xl px-4 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
              >
                Ahora no
              </button>
            </div>
          </div>
        </div>
      )}

      {showIOSInstructions && <IOSInstructionsModal onClose={() => setShowIOSInstructions(false)} />}
      {showSamsungInfo && <SamsungInternetModal onClose={() => setShowSamsungInfo(false)} />}
    </>
  );
}

export default PWAInstallPrompt;
